import { useEffect } from "react";
import { produce } from "immer";
import { CircleQuestionMarkIcon } from "lucide-react";

import { getText } from "shared/lib";
import {
  TreePropertyType,
  updateTreeProperty,
  type SizeEstimateTreePropertyDescriptor,
  type TreeDescriptor,
  type TrunkTypeTreePropertyDescriptor,
} from "./lib";

import { Button, Separator, TooltipProvider } from "shared/components";
import { TreeDialog2 } from "./treeDialog";
import { SizeEstimateField } from "./treeDialog/sizeEstimateField/SizeEstimateField";
import { IsNativeSpeciesField } from "./treeDialog/IsNativeSpeciesField";
import { TrunkTypeField } from "./treeDialog/TrunkTypeField";
import { MultiStemTreeDiagram } from "./treeDialog/multiStemTreeDiagram";
import { TreeDiagram } from "./treeDialog/treeDiagram";
import { DBHField } from "./treeDialog/DBHField";
import { CircumferenceField } from "./treeDialog/CircumferenceField";
import { RemoveOrPruneField } from "./treeDialog/RemoveOrPruneField";
import { RemoveOrPruneReasonField } from "./treeDialog/RemoveOrPruneReasonField";
import { HeightField } from "./treeDialog/HeightField";
import { MeasurementTypeField } from "./treeDialog/MeasurementTypeField";
import { PruningQuantityField } from "@/components/treeModule/treeDialog/PruningQuantityField";
import { TreeMeasurementsDialogContainer } from "./treeMeasurementsHelp";
import { PropertyBoundaryField } from "@/components/treeModule/treeDialog/PropertyBoundaryField";
import { CanopySizeField } from "@/components/treeModule/treeDialog/CanopySizeField";

export interface RemovingPruningTreeDialogProps {
  onChangeTreeDescriptors?: (treeDescriptors: Array<TreeDescriptor>) => void;
  onClickSubmit?: (treeDescriptor: TreeDescriptor) => void;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  treeDescriptor: TreeDescriptor | undefined | null;
  treeDescriptors: Array<TreeDescriptor>;
}

export function RemovingOrPruningTreeDialog({
  onChangeTreeDescriptors,
  onClickSubmit,
  onOpenChange,
  open,
  treeDescriptor,
  treeDescriptors,
}: RemovingPruningTreeDialogProps) {
  const dbhPropertyExists = treeDescriptor?.properties.has(
    TreePropertyType.dbh,
  );

  const circumferencePropertyExists = treeDescriptor?.properties.has(
    TreePropertyType.circumference,
  );

  const heightPropertyExists = treeDescriptor?.properties.has(
    TreePropertyType.height,
  );

  const treeMeasurementsType = treeDescriptor?.properties.get(
    TreePropertyType.measurement_type,
  );

  const size = treeDescriptor?.properties.get(TreePropertyType.size) as
    | SizeEstimateTreePropertyDescriptor
    | undefined;

  const trunkType = treeDescriptor?.properties.get(TreePropertyType.trunk_type);

  const dbh = treeDescriptor?.properties.get(TreePropertyType.dbh);

  const circumference = treeDescriptor?.properties.get(
    TreePropertyType.circumference,
  );

  const distance = treeDescriptor?.properties.get(TreePropertyType.distance);

  const removeOrPrune = treeDescriptor?.properties.get(
    TreePropertyType.remove_or_prune,
  );

  const handleChangeTreeDescriptor = (
    changedTreeDescriptor: TreeDescriptor,
  ) => {
    onChangeTreeDescriptors?.(
      produce(treeDescriptors, (draft) => {
        const index = draft.findIndex(
          (td) => td.treeId === changedTreeDescriptor?.treeId,
        );
        if (index > -1) {
          draft[index] = { ...changedTreeDescriptor };
        }
      }),
    );
  };

  useEffect(() => {
    if (treeDescriptor && treeMeasurementsType?.value === "estimate") {
      const sizeEstimate = treeDescriptor.properties.get(
        TreePropertyType.size,
      ) as SizeEstimateTreePropertyDescriptor | undefined;

      if (sizeEstimate?.value) {
        const trunkTypeValue =
          sizeEstimate.value === "vegetation" ? "vegetation" : "single";

        const newTreeDescriptor =
          updateTreeProperty<TrunkTypeTreePropertyDescriptor>(
            treeDescriptor,
            TreePropertyType.trunk_type,
            (draft) => {
              draft.value =
                trunkTypeValue as TrunkTypeTreePropertyDescriptor["value"];
            },
          );
        handleChangeTreeDescriptor(newTreeDescriptor);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeDescriptor?.treeId, treeMeasurementsType, size]);

  const renderTreeDiagram = () => {
    let diameter = 0.1;
    let label = "";
    if (dbh?.value) {
      diameter = parseFloat(dbh.value);
      label = `${diameter.toFixed(2)}m`;
    } else if (circumference?.value) {
      const c = parseFloat(circumference.value);
      diameter = (c / 3.5) * 1.5;
      label = `${c.toFixed(2)}m`;
    }

    return trunkType?.value === "multi" ? (
      <MultiStemTreeDiagram
        label={label}
        treeDiameter={diameter}
        treeDistance={parseInt(distance?.value || "1") - 1}
      />
    ) : (
      <TreeDiagram
        label={label}
        treeDiameter={diameter}
        treeDistance={parseInt(distance?.value || "1") - 1}
      />
    );
  };

  const renderTreeMeasurementsHelp = () => {
    return (
      <TreeMeasurementsDialogContainer treeDescriptor={treeDescriptor}>
        {(setOpen) => (
          <Button
            className="text-bodysm w-min gap-1"
            onClick={() => setOpen(true)}
            size="tight"
            variant="link"
          >
            <CircleQuestionMarkIcon className="size-4" />{" "}
            {getText("How to measure a tree")}
          </Button>
        )}
      </TreeMeasurementsDialogContainer>
    );
  };

  if (!treeDescriptor) return;

  return (
    <TooltipProvider>
      <TreeDialog2
        onClickSubmit={onClickSubmit}
        onOpenChange={onOpenChange}
        open={open}
        treeDescriptor={treeDescriptor}
      >
        <div>
          <h3 className="text-bodylg text-textPrimary">
            {getText("How large is this tree/vegetation?")}
          </h3>
          <p className="text-bodymd text-textSubdued">
            {getText(
              "Providing detailed measurements will help us give you a more accurate result.",
            )}
          </p>
        </div>

        <MeasurementTypeField
          onChange={handleChangeTreeDescriptor}
          treeDescriptor={treeDescriptor}
        />

        {treeMeasurementsType?.value === "estimate" && (
          <>
            <SizeEstimateField
              includeVegetationItem
              onChange={handleChangeTreeDescriptor}
              treeDescriptor={treeDescriptor}
            />

            {["medium", "large"].includes(size?.value || "") && (
              <PropertyBoundaryField
                onChange={handleChangeTreeDescriptor}
                treeDescriptor={treeDescriptor}
              />
            )}

            {size?.value && (
              <>
                <IsNativeSpeciesField
                  onChange={handleChangeTreeDescriptor}
                  treeDescriptor={treeDescriptor}
                />

                <RemoveOrPruneField
                  onChange={handleChangeTreeDescriptor}
                  treeDescriptor={treeDescriptor}
                />

                {removeOrPrune?.value === "prune" && (
                  <PruningQuantityField
                    onChange={handleChangeTreeDescriptor}
                    treeDescriptor={treeDescriptor}
                  />
                )}

                {!!removeOrPrune?.value && (
                  <RemoveOrPruneReasonField
                    onChange={handleChangeTreeDescriptor}
                    treeDescriptor={treeDescriptor}
                  />
                )}
              </>
            )}
          </>
        )}

        {treeMeasurementsType?.value === "detailed" && (
          <>
            <TrunkTypeField
              includeVegetation
              label={getText("Tree or vegetation type")}
              onChange={handleChangeTreeDescriptor}
              treeDescriptor={treeDescriptor}
            />

            {["single", "multi"].includes(trunkType?.value || "") && (
              <>
                {renderTreeDiagram()}

                {dbhPropertyExists && (
                  <DBHField
                    hideMeasurementEstimationAccordion
                    isMultiStem={trunkType?.value === "multi"}
                    onChange={handleChangeTreeDescriptor}
                    treeDescriptor={treeDescriptor}
                  />
                )}

                {circumferencePropertyExists && (
                  <CircumferenceField
                    hideMeasurementEstimationAccordion
                    isMultiStem={trunkType?.value === "multi"}
                    onChange={handleChangeTreeDescriptor}
                    treeDescriptor={treeDescriptor}
                  />
                )}

                {heightPropertyExists && (
                  <HeightField
                    onChange={handleChangeTreeDescriptor}
                    treeDescriptor={treeDescriptor}
                  />
                )}

                <CanopySizeField
                  onChange={handleChangeTreeDescriptor}
                  treeDescriptor={treeDescriptor}
                />

                {renderTreeMeasurementsHelp()}

                <Separator />

                <PropertyBoundaryField
                  onChange={handleChangeTreeDescriptor}
                  treeDescriptor={treeDescriptor}
                />
              </>
            )}

            <IsNativeSpeciesField
              onChange={handleChangeTreeDescriptor}
              treeDescriptor={treeDescriptor}
            />

            <RemoveOrPruneField
              onChange={handleChangeTreeDescriptor}
              treeDescriptor={treeDescriptor}
            />

            {removeOrPrune?.value === "prune" && (
              <PruningQuantityField
                onChange={handleChangeTreeDescriptor}
                treeDescriptor={treeDescriptor}
              />
            )}

            {!!removeOrPrune?.value && (
              <RemoveOrPruneReasonField
                onChange={handleChangeTreeDescriptor}
                treeDescriptor={treeDescriptor}
              />
            )}
          </>
        )}
      </TreeDialog2>
    </TooltipProvider>
  );
}
